import React, { useState, useEffect } from "react";
import axios from "axios";
import "./ProductManager.css";
import { useSelector } from "react-redux";

function ProductValises({ onProductAdded, onSubProductAdded }) {
  const [newProductName, setNewProductName] = useState("");
  const [newProductCategory, setNewProductCategory] = useState("VALISES");
  const [newSubProductName, setNewSubProductName] = useState("");
  const [newSubProductPrice, setNewSubProductPrice] = useState("");
  const [newSubProductBarcode, setNewSubProductBarcode] = useState("");
  const [newSubProductImage, setNewSubProductImage] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);

  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // États pour la création de SET (composition GRAND / MOYEN / PETIT)
  const [setPrice, setSetPrice] = useState("");
  const [allSubProductsForSet, setAllSubProductsForSet] = useState([]);
  const [selectedSetSubProducts, setSelectedSetSubProducts] = useState([]);
  const [setImageFile, setSetImageFile] = useState(null);

  const currentUser = useSelector((state) => state.AuthReducer?.user);

  useEffect(() => {
    axios.defaults.timeout = 30000;

    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        console.log(`🔄 ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      }
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        console.log(`✅ ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        console.log(`⚠️ Erreur API ${error.response?.status}: ${error.config?.url}`);
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === 'string') return loc.trim();
    if (loc.$oid) return loc.$oid.toString();
    if (loc._id) return loc._id.toString();
    if (loc.id) return loc.id.toString();
    return null;
  };

  // 🔄 Charger les produits
  useEffect(() => {
    let isMounted = true;

    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("token");
        const response = await axios.get("/api/ProductV/getProductV", {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
          timeout: 15000
        });

        if (isMounted) {
          setProducts(response.data);
        }
      } catch (error) {
        console.error("Erreur chargement produits:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  // 🆕 Charger les localisations
  useEffect(() => {
    let isMounted = true;

    const fetchLocations = async () => {
      try {
        const res = await axios.get("/api/Locations/getAllLocations", {
          timeout: 15000
        });

        if (isMounted) {
          setLocations(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        console.error("Erreur chargement emplacements:", err);
        setLocations([]);
      }
    };

    fetchLocations();

    return () => {
      isMounted = false;
    };
  }, []);

  // 🔥 CORRECTION : Définir les localisations par défaut - useradmin = admin
  useEffect(() => {
    if (currentUser?.role === "admin" || currentUser?.role === "useradmin") {
      return;
    }

    if (currentUser?.localisation) {
      const userLocId = extractLocId(currentUser.localisation._id);
      setSelectedLocations([userLocId]);
    }
  }, [currentUser]);

  // Charger les sous-produits SubProductV pour composer un SET
  // Filtrer : catégorie VALISES (via produit parent) + localisation BNG-Tunisia Mall
  useEffect(() => {
    const fetchSubProductsForSet = async () => {
      try {
        const res = await axios.get("/api/ProductV/getAllSubProductV", {
          timeout: 15000,
        });
        const rawList = Array.isArray(res.data?.subProducts) ? res.data.subProducts : [];
        // Id de la localisation BNG-Tunisia Mall (depuis la liste des locations déjà chargée)
        const bngLoc = locations.find(
          (l) => (l.name || "").trim().toUpperCase() === "BNG-TUNISIA MALL"
        );
        const bngId = bngLoc ? extractLocId(bngLoc._id) : null;
        const list = bngId
          ? rawList.filter((sp) => {
              const spLocId = extractLocId(sp.localisation?._id || sp.localisation);
              return spLocId === bngId;
            })
          : rawList;
        setAllSubProductsForSet(list);
      } catch (err) {
        console.error("Erreur chargement sous-produits pour SET:", err);
        setAllSubProductsForSet([]);
      }
    };
    fetchSubProductsForSet();
  }, [locations]);

  const toggleSetSubProduct = (id) => {
    setSelectedSetSubProducts((prev) =>
      prev.includes(id) ? prev.filter((sp) => sp !== id) : [...prev, id]
    );
  };

  const addSetProductHandler = async () => {
    if (
      !newProductName.trim() ||
      !setPrice ||
      selectedSetSubProducts.length !== 3
    ) {
      alert(
        "Pour un SET : nom, prix et exactement 3 sous-produits (1 GRAND, 1 MOYEN, 1 PETIT) sont requis."
      );
      return;
    }
    const priceNum = parseFloat(setPrice);
    if (isNaN(priceNum) || priceNum <= 0) {
      alert("Le prix du SET doit être un nombre positif.");
      return;
    }
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");

      // Préparer l'image du SET en base64 (max ~128 KB)
      let base64Image = "";
      if (setImageFile) {
        base64Image = await compressImage(setImageFile, 128);
      }

      // Créer le SET dans ProductV avec subProductsV (SubProductV)
      const payload = {
        name: newProductName.trim(),
        category: "SET",
        subProductsV: selectedSetSubProducts,
        price: priceNum,
        image: base64Image,
      };
      const response = await axios.post("/api/ProductV/AddProductV", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });
      setNewProductName("");
      setSetPrice("");
      setSelectedSetSubProducts([]);
      setSetImageFile(null);
      setNewProductCategory("VALISES");
      setProducts((prev) => [...prev, response.data]);
      if (onProductAdded) onProductAdded(response.data);
      alert("✅ SET créé avec succès !");
    } catch (error) {
      console.error("Erreur création SET:", error);
      alert(
        "❌ Erreur lors de la création du SET. " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getAvailableLocations = () => {
    if (!currentUser) return [];
    if (!Array.isArray(locations)) return [];

    if (currentUser.role === "admin" || currentUser.role === "useradmin") {
      return locations;
    } else {
      const userLocId = extractLocId(currentUser.localisation?._id);
      return locations.filter(loc => {
        const locId = extractLocId(loc._id);
        return locId === userLocId;
      });
    }
  };

  const availableLocations = getAvailableLocations();

  const handleLocationChange = (locationId) => {
    setSelectedLocations(prev => {
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  const toggleAllLocations = () => {
    if (selectedLocations.length === availableLocations.length) {
      setSelectedLocations([]);
    } else {
      const allLocationIds = availableLocations.map(loc => extractLocId(loc._id)).filter(id => id !== null);
      setSelectedLocations(allLocationIds);
    }
  };

  const compressImage = (file, targetKB) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          const MAX_DIM = 800;
          if (width > height && width > MAX_DIM) {
            height = (height * MAX_DIM) / width;
            width = MAX_DIM;
          } else if (height > MAX_DIM) {
            width = (width * MAX_DIM) / height;
            height = MAX_DIM;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          let quality = 0.8;
          let base64 = canvas.toDataURL("image/jpeg", quality);
          if (base64.length > targetKB * 1.33 * 1024) {
            quality = 0.5;
            base64 = canvas.toDataURL("image/jpeg", quality);
          }
          resolve(base64);
        };
      };
    });
  };

  const addProductHandler = async () => {
    if (!newProductName.trim()) {
      alert("Veuillez entrer un nom pour le produit.");
      return;
    }

    try {
      setIsLoading(true);
      let base64Image = "";
      if (newSubProductImage) {
        base64Image = await compressImage(newSubProductImage, 128);
      }
      const token = localStorage.getItem("token");
      const response = await axios.post("/api/ProductV/AddProductV", {
        name: newProductName,
        category: newProductCategory,
        image: base64Image
      }, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        }
      });

      setNewProductName("");
      setNewProductCategory("VALISES");
      setNewSubProductImage(null);
      setProducts((prev) => [...prev, response.data]);
      if (onProductAdded) onProductAdded(response.data);
      alert("✅ Produit ajouté avec succès !");
    } catch (error) {
      console.error("Erreur ajout produit:", error);
      alert("❌ Erreur lors de l'ajout du produit.");
    } finally {
      setIsLoading(false);
    }
  };

  const checkBarcodeInLocation = (barcode, locationId) => {
    if (!barcode.trim()) return false;
    const existsInSameLocation = products.some(product =>
      product.subProductsV?.some(subProduct => {
        const subProductLocations = subProduct.localisations || [subProduct.localisation].filter(Boolean);
        const hasSameLocation = subProductLocations.some(loc =>
          extractLocId(loc) === locationId
        );
        return subProduct.barcode === barcode && hasSameLocation;
      })
    );
    return existsInSameLocation;
  };

  const addSubProductHandler = async () => {
    if (!selectedProduct || !newSubProductName.trim() || !newSubProductPrice || selectedLocations.length === 0) {
      alert("Veuillez sélectionner un produit, entrer un nom, un prix et sélectionner au moins une localisation.");
      return;
    }

    const price = parseFloat(newSubProductPrice);
    if (isNaN(price) || price <= 0) {
      alert("❌ Le prix doit être un nombre positif.");
      return;
    }

    const validLocations = selectedLocations.filter(locId => {
      return availableLocations.some(loc => extractLocId(loc._id) === locId);
    });

    if (validLocations.length === 0) {
      alert("❌ Aucune localisation valide sélectionnée.");
      return;
    }

    const barcodeTrimmed = newSubProductBarcode.trim();
    const locationsWithBarcodeIssues = [];

    if (barcodeTrimmed) {
      validLocations.forEach(locationId => {
        if (checkBarcodeInLocation(barcodeTrimmed, locationId)) {
          const location = availableLocations.find(loc => extractLocId(loc._id) === locationId);
          locationsWithBarcodeIssues.push(location?.name || locationId);
        }
      });

      if (locationsWithBarcodeIssues.length > 0) {
        alert(`❌ Ce code-barres existe déjà dans les localisations suivantes: ${locationsWithBarcodeIssues.join(', ')}\n\nUn même code-barres ne peut pas être utilisé dans la même localisation.`);
        return;
      }
    }

    try {
      setIsLoading(true);
      let base64Image = "";
      if (newSubProductImage) {
        base64Image = await compressImage(newSubProductImage, 128);
      }

      const token = localStorage.getItem("token");
      const results = [];

      for (const locationId of validLocations) {
        try {
          const payload = {
            name: newSubProductName.trim(),
            price: price,
            barcode: barcodeTrimmed || "",
            localisation: locationId,
            image: base64Image
          };

          const response = await axios.post(
            `/api/ProductV/${selectedProduct}/subproduct`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": token ? `Bearer ${token}` : ""
              },
              timeout: 30000
            }
          );
          results.push({ success: true, locationId, data: response.data });
        } catch (error) {
          results.push({
            success: false,
            locationId,
            error: error.response?.data?.message || error.message
          });
        }
      }

      const successful = results.filter(r => r.success);
      if (successful.length > 0) {
        setNewSubProductName("");
        setNewSubProductPrice("");
        setNewSubProductBarcode("");
        setNewSubProductImage(null);
        if (currentUser?.role !== "admin" && currentUser?.role !== "useradmin") {
          setSelectedLocations([]);
        }
        if (onSubProductAdded) onSubProductAdded();
        alert(`✅ ${successful.length} sous-produit(s) ajouté(s) !`);

        // Rafraîchir les produits
        const refreshResponse = await axios.get("/api/ProductV/getProductV", {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
          timeout: 15000
        });
        setProducts(refreshResponse.data);
      }
    } catch (globalError) {
      alert("❌ Erreur critique lors de la création.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBarcodeTest = async () => {
    if (!newSubProductBarcode.trim()) {
      alert("Veuillez entrer un code-barres à tester");
      return;
    }
    try {
      const response = await axios.get(`/api/ProductV/findByBarcode/${newSubProductBarcode.trim()}`, {
        headers: { Authorization: localStorage.getItem("token") ? `Bearer ${localStorage.getItem("token")}` : "" }
      });
      if (response.data) {
        alert(`✅ Produit trouvé: ${response.data.name} - ${response.data.price}€`);
        setNewSubProductName(response.data.name);
        setNewSubProductPrice(response.data.price.toString());
      } else {
        alert("❌ Aucun produit trouvé.");
      }
    } catch (e) {
      alert("❌ Aucun produit trouvé ou erreur serveur.");
    }
  };

  if (!currentUser) return null;

  return (
    <div className="product-manager pv-two-columns">
      <div className="user-info">
        <small><strong>{currentUser.name}</strong> ({currentUser.role}) - {currentUser.localisation?.name || "Sans lieu"}</small>
      </div>

      {isLoading && <div className="loading-overlay">Chargement...</div>}

      <div className="pv-grid-container">
        {/* Colonne Gauche : Produit */}
        <div className="pv-column">
          <h3>Ajouter un Produit</h3>
          <input type="text" value={newProductName} onChange={(e) => setNewProductName(e.target.value)} placeholder="Nom du produit" />
          <select value={newProductCategory} onChange={(e) => { setNewProductCategory(e.target.value); if (e.target.value !== "SET") { setSelectedSetSubProducts([]); setSetPrice(""); } }}>
            <option value="VALISES">VALISES</option>
            <option value="SACS">SACS</option>
            <option value="ACCESSOIRES">ACCESSOIRES</option>
            <option value="SET">SET</option>
            <option value="TRANSPORT">TRANSPORT</option>
            <option value="POUPEE">POUPEE</option>
            <option value="EDUCATIFS">EDUCATIFS</option>
            <option value="METIERS">METIERS</option>
            <option value="BRICOLAGE">BRICOLAGE</option>
            <option value="PERSONAGE">PERSONAGE</option>
            <option value="ANIMAUX">ANIMAUX</option>
            <option value="FILLETTE">FILLETTE</option>
            <option value="CUISINE">CUISINE</option>
            <option value="MUSIQUE">MUSIQUE</option>
            <option value="FIRSTAGE">FIRSTAGE</option>
            <option value="CANDY STORE">CANDY STORE</option>
            <option value="PLAYSTATION">PLAYSTATION</option>
          </select>

          {newProductCategory === "SET" && (
            <div className="set-composition" style={{ marginTop: "12px", padding: "10px", border: "1px solid #ddd", borderRadius: "8px", background: "#f9f9f9" }}>
              <h4 style={{ margin: "0 0 8px 0", fontSize: "14px" }}>Composition du SET (GRAND + MOYEN + PETIT)</h4>
              <input
                type="number"
                step="0.01"
                min="0"
                value={setPrice}
                onChange={(e) => setSetPrice(e.target.value)}
                placeholder="Prix du SET"
                style={{ width: "100%", marginBottom: "8px", padding: "8px", boxSizing: "border-box" }}
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setSetImageFile(e.target.files[0] || null)}
                style={{ width: "100%", marginBottom: "8px" }}
              />
              <div className="set-subproducts-grid" style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "6px" }}>
                {allSubProductsForSet.map((sp) => (
                  <button
                    key={sp._id}
                    type="button"
                    className={selectedSetSubProducts.includes(sp._id) ? "selected" : ""}
                    onClick={() => toggleSetSubProduct(sp._id)}
                    style={{
                      padding: "6px 10px",
                      fontSize: "12px",
                      borderRadius: "6px",
                      border: selectedSetSubProducts.includes(sp._id) ? "2px solid #7B1FA2" : "1px solid #ccc",
                      background: selectedSetSubProducts.includes(sp._id) ? "#F3E5F5" : "#fff",
                      cursor: "pointer",
                      color: "#000",
                    }}
                  >
                    {sp.name}
                  </button>
                ))}
              </div>
              <small style={{ color: "#666" }}>
                Sélectionnez exactement 3 valises : 1 GRAND, 1 MOYEN, 1 PETIT (ex. SET BLANC).
              </small>
            </div>
          )}

          <button
            onClick={newProductCategory === "SET" ? addSetProductHandler : addProductHandler}
            disabled={isLoading || (newProductCategory === "SET" && selectedSetSubProducts.length !== 3)}
          >
            {newProductCategory === "SET" ? "Créer le SET" : "Ajouter Produit"}
          </button>
        </div>

        {/* Colonne Droite : Sous-Produit */}
        <div className="pv-column">
          <h3>Ajouter un Sous-Produit</h3>
          <select value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)}>
            <option value="">Sélectionner un produit</option>
            {products.map((p) => <option key={p._id} value={p._id}>{p.name} ({p.category})</option>)}
          </select>

          <div className="barcode-section">
            <input type="text" value={newSubProductBarcode} onChange={(e) => setNewSubProductBarcode(e.target.value)} placeholder="Code-barres" />
            <button type="button" onClick={handleBarcodeTest} className="test-barcode-btn">🔍 Tester</button>
          </div>

          <input type="text" value={newSubProductName} onChange={(e) => setNewSubProductName(e.target.value)} placeholder="Nom du sous-produit" />
          <input type="number" value={newSubProductPrice} onChange={(e) => setNewSubProductPrice(e.target.value)} placeholder="Prix" />
          <input type="file" accept="image/*" onChange={(e) => setNewSubProductImage(e.target.files[0])} />

          <div className="locations-section">
            <div className="locations-header">
              <h4>Localisations *</h4>
              <button type="button" onClick={toggleAllLocations}>{selectedLocations.length === availableLocations.length ? 'Tout désélectionner' : 'Tout sélectionner'}</button>
            </div>
            <div className="locations-grid">
              {availableLocations.map(loc => {
                const id = extractLocId(loc._id);
                const isSelected = selectedLocations.includes(id);
                return (
                  <div key={id} className={`location-card ${isSelected ? 'selected' : ''}`} onClick={() => handleLocationChange(id)}>
                    {loc.name}
                  </div>
                );
              })}
            </div>
          </div>

          <button className="add-subproduct-btn" onClick={addSubProductHandler} disabled={isLoading || selectedLocations.length === 0}>
            Créer dans {selectedLocations.length} lieu(x)
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductValises;
